import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import {
    Container,
    Typography,
    CircularProgress,
    Button,
    Box,
    Paper,
    useTheme,
    Fade,
    Alert,
    Collapse,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import { useLanguage } from './LanguageContext';

export default function JoinClassPage() {
    const { joinCode } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useLanguage();

    const gradientBackground = {
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    };

    useEffect(() => {
        document.title = `${t.joiningClass} | ClassCapture`;
    }, [t.joiningClass]);

    useEffect(() => {
        const joinClass = async () => {
            if (!joinCode) {
                setError(t.noJoinCodeProvided);
                setLoading(false);
                return;
            }

            try {
                const auth = getAuth();
                const token = await auth.currentUser.getIdToken();
                const url = `https://classroomservices-470732867211.us-central1.run.app/api/classroom/join`;

                const response = await axios.post(url, { joinCode: joinCode }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.error) {
                    setError(response.data.error);
                } else {
                    setSuccess(true);
                    setTimeout(() => {
                        navigate(-1);
                    }, 1500);
                }
            } catch (err) {
                console.error("Error joining class:", err);
                setError(err.response?.data?.error || t.joinClassError);
            } finally {
                setLoading(false);
            }
        };

        joinClass();
    }, [joinCode, navigate, t]);

    const handleBackToDashboard = () => {
        navigate('/dashboard');
    };

    return (
        <Container component="main" maxWidth="sm" sx={{ py: 4 }}>
            <Fade in={true} timeout={450}>
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        borderRadius: 2,
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            ...gradientBackground
                        }
                    }}
                >
                    {loading ? (
                        <>
                            <CircularProgress size={60} thickness={4} sx={{ mb: 3, color: theme.palette.primary.main }} />
                            <Typography variant="h5" component="h1" gutterBottom>
                                {t.joiningClass}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t.joiningClassDescription}
                            </Typography>
                        </>
                    ) : error ? (
                        <>
                            <Box sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                bgcolor: theme.palette.error.light,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2
                            }}>
                                <SchoolIcon sx={{ fontSize: 30, color: theme.palette.error.main }} />
                            </Box>
                            <Typography variant="h5" component="h1" gutterBottom>
                                {t.joinClassFailed}
                            </Typography>
                            <Collapse in={!!error} sx={{ width: '100%', mb: 2 }}>
                                <Alert
                                    severity="error"
                                    action={
                                        <IconButton
                                            aria-label="close"
                                            color="inherit"
                                            size="small"
                                            onClick={() => setError('')}
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </IconButton>
                                    }
                                    sx={{ mb: 2 }}
                                >
                                    {error}
                                </Alert>
                            </Collapse>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleBackToDashboard}
                                sx={{ mt: 1 }}
                            >
                                {t.backToDashboard}
                            </Button>
                        </>
                    ) : success ? (
                        <>
                            <Box sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                bgcolor: theme.palette.success.light,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2
                            }}>
                                <SchoolIcon sx={{ fontSize: 30, color: theme.palette.success.main }} />
                            </Box>
                            <Typography variant="h5" component="h1" gutterBottom>
                                {t.joinClassSuccess}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t.redirectingToClass}
                            </Typography>
                            <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
                        </>
                    ) : null}
                </Paper>
            </Fade>
        </Container>
    );
}